import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, Clock, Link as LinkIcon, PackageCheck, Send, Plus, Download, FileText,
  Camera, Upload, Trash2, AlertTriangle, Loader2, ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';
import { toastApiError } from '@/lib/errors';
import StatusBadge from '@/components/StatusBadge';
import RefLink from '@/components/RefLink';
import EmptyState from '@/components/EmptyState';
import LoadingState from '@/components/LoadingState';

// Route /intakes/:id, read-only detail.
// Tabs: Přehled / Historie / Související.
// Edit probíhá v /intakes split-view (open detail z IntakeRegistryPage header).

const TABS = [
  { id: 'overview', labelKey: 'intakes.detail-tab-overview', icon: PackageCheck },
  { id: 'progress', labelKey: 'intakes.detail-tab-progress', icon: ClipboardList },
  { id: 'photos', labelKey: 'intakes.detail-tab-photos', icon: Camera },
  { id: 'history', labelKey: 'intakes.detail-tab-history', icon: Clock },
  { id: 'related', labelKey: 'intakes.detail-tab-related', icon: LinkIcon },
];

// Stav kalibrací příjmu
function ProgressTab({ intake }) {
  const { t } = useTranslation();
  const [calibrations, setCalibrations] = useState([]);
  const [masterCerts, setMasterCerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get(`/calibrations?intakeId=${intake.id}`).catch(() => ({ data: [] })),
      api.get(`/master-certificates`).catch(() => ({ data: [] })),
    ]).then(([calsRes, mcRes]) => {
      if (cancelled) return;
      setCalibrations((calsRes.data || []).filter(c => c.intakeId === intake.id));
      setMasterCerts((mcRes.data || []).filter(m => m.intakeId === intake.id));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [intake.id]);

  if (loading) return <div className="text-sm" style={{ color: 'var(--ct-text-secondary)' }}>{t('intakes.progress-loading')}</div>;

  // Group calibrations by instrumentId
  const byInstrument = {};
  for (const c of calibrations) {
    const k = c.instrumentId || '_unknown';
    if (!byInstrument[k]) {
      byInstrument[k] = {
        instrumentName: c.instrumentName,
        instrumentType: c.instrumentType,
        calibrations: [],
      };
    }
    byInstrument[k].calibrations.push(c);
  }

  const masterByInstrument = Object.fromEntries(masterCerts.map(m => [m.instrumentId, m]));

  const statusColor = (s) => {
    if (s === 'completed' || s === 'approved') return 'var(--ct-success)';
    if (s === 'in_progress' || s === 'under_technical_review') return 'var(--ct-warning)';
    if (s === 'rejected') return 'var(--ct-error)';
    return 'var(--ct-text-secondary)';
  };

  return (
    <div className="space-y-4">
      <div className="text-sm" style={{ color: 'var(--ct-text-secondary)' }}>
        {t('intakes.progress-help')}
      </div>
      {Object.entries(byInstrument).length === 0 && (
        <EmptyState
          icon={ClipboardList}
          title={t('intakes.progress-empty-title')}
          description={t('intakes.progress-empty-desc')}
        />
      )}
      {Object.entries(byInstrument).map(([instrumentId, group]) => {
        const total = group.calibrations.length;
        const completed = group.calibrations.filter(c => c.status === 'completed').length;
        const mc = masterByInstrument[instrumentId];
        const progressPct = total > 0 ? (completed / total) * 100 : 0;
        return (
          <div key={instrumentId} className="ct-panel p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-medium">{group.instrumentName || t('intakes.progress-instrument-fallback')}</div>
                <div className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
                  {group.instrumentType || ''}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-mono">
                  {t('intakes.progress-probes-count', { completed, total, count: total })}
                </div>
                {mc && (
                  <div className="text-xs mt-1">
                    <StatusBadge status={mc.status} />
                    {mc.certificateNumber && (
                      <span className="ml-2 font-mono" style={{ color: 'var(--ct-success)' }}>
                        {mc.certificateNumber}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Progress bar */}
            <div className="h-2 rounded-full mb-3" style={{ background: 'var(--ct-bg-secondary)' }}>
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${progressPct}%`,
                  background: progressPct === 100 ? 'var(--ct-success)' : 'var(--ct-accent)',
                }}
              />
            </div>
            {/* Per-probe list */}
            <div className="space-y-1 text-xs">
              {group.calibrations.map(c => (
                <div key={c.id} className="flex items-center justify-between py-1"
                  style={{ borderTop: '1px solid var(--ct-border)' }}>
                  <div className="flex items-center gap-2">
                    <span style={{ color: 'var(--ct-text-secondary)' }}>{c.laboratory}</span>
                    {c.probeName && <span>· {c.probeName}</span>}
                    {c.calibratingUserName && (
                      <span style={{ color: 'var(--ct-text-disabled)' }}>· {c.calibratingUserName}</span>
                    )}
                  </div>
                  <span style={{ color: statusColor(c.status) }}>
                    <StatusBadge status={c.status} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Vizuální kontrola + zjištěné vady
function PhotosTab({ intakeId }) {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState('');
  const [defectType, setDefectType] = useState('');
  const [severity, setSeverity] = useState('');

  const reload = useCallback(() => {
    api.get(`/intakes/${intakeId}/photos`).then(r => setPhotos(r.data)).catch(() => setPhotos([]));
  }, [intakeId]);

  useEffect(() => { reload(); }, [reload]);

  const photoUrl = (photoId) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('ct-token');
    return `${baseUrl}/api/intakes/${intakeId}/photos/${photoId}?_=${token?.slice(-8)}`;
  };

  // Auth-aware blob URL — fetch s tokenem v Authorization header (browser cache).
  const [blobUrls, setBlobUrls] = useState({});
  useEffect(() => {
    const token = localStorage.getItem('ct-token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    photos.forEach(p => {
      if (blobUrls[p.id]) return;
      fetch(`${baseUrl}/api/intakes/${intakeId}/photos/${p.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.blob() : null)
        .then(blob => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            setBlobUrls(prev => ({ ...prev, [p.id]: url }));
          }
        });
    });
    // Cleanup on unmount
    return () => {
      Object.values(blobUrls).forEach(url => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos, intakeId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('intakes.photos-error-too-large', { kb: Math.round(file.size / 1024) }));
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('description', description);
      fd.append('defect_type', defectType);
      fd.append('severity', severity);
      await api.post(`/intakes/${intakeId}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('intakes.photos-toast-uploaded'));
      setDescription('');
      setDefectType('');
      setSeverity('');
      reload();
    } catch (err) {
      toastApiError(err, t('intakes.photos-error-upload'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm(t('intakes.photos-confirm-delete'))) return;
    try {
      await api.delete(`/intakes/${intakeId}/photos/${photoId}`);
      toast.success(t('intakes.photos-toast-deleted'));
      reload();
    } catch (err) {
      toastApiError(err);
    }
  };

  const severityColor = (sev) => {
    if (sev === 'high') return 'var(--ct-error)';
    if (sev === 'medium') return 'var(--ct-warning)';
    if (sev === 'low') return 'var(--ct-info)';
    return 'var(--ct-text-secondary)';
  };
  const severityLabel = (sev) => ({
    low: t('intakes.photos-severity-low-inline'),
    medium: t('intakes.photos-severity-medium-inline'),
    high: t('intakes.photos-severity-high-inline'),
  }[sev] || sev);

  return (
    <div className="space-y-4">
      {/* Upload form */}
      <div className="ct-panel p-4 space-y-3">
        <h3 className="ct-eyebrow">{t('intakes.photos-upload-title')}</h3>
        <div className="grid grid-cols-3 gap-2">
          <Input
            placeholder={t('intakes.photos-desc-placeholder')}
            value={description}
            onChange={e => setDescription(e.target.value)}
            className="text-sm"
          />
          <Input
            placeholder={t('intakes.photos-defect-placeholder')}
            value={defectType}
            onChange={e => setDefectType(e.target.value)}
            className="text-sm"
          />
          <select
            value={severity}
            onChange={e => setSeverity(e.target.value)}
            className="ct-input text-sm"
          >
            <option value="">{t('intakes.photos-severity-none')}</option>
            <option value="low">{t('intakes.photos-severity-low')}</option>
            <option value="medium">{t('intakes.photos-severity-medium')}</option>
            <option value="high">{t('intakes.photos-severity-high')}</option>
          </select>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic"
            onChange={handleUpload}
            disabled={uploading}
            className="hidden"
            data-testid="photo-upload-input"
          />
          <Button asChild className="ct-btn-primary gap-1 cursor-pointer" disabled={uploading}>
            <span>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {t('intakes.photos-pick-file')}
            </span>
          </Button>
        </label>
        <p className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
          {t('intakes.photos-help')}
        </p>
      </div>

      {/* Gallery */}
      {photos.length === 0 ? (
        <EmptyState
          icon={Camera}
          title={t('intakes.photos-empty-title')}
          description={t('intakes.photos-empty-desc')}
        />
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {photos.map(p => (
            <div key={p.id} className="ct-panel overflow-hidden">
              <div className="aspect-video flex items-center justify-center"
                style={{ background: 'var(--ct-bg-secondary)' }}>
                {blobUrls[p.id] ? (
                  <a href={blobUrls[p.id]} target="_blank" rel="noopener noreferrer">
                    <img src={blobUrls[p.id]} alt={p.description || p.filename}
                      className="max-h-full max-w-full object-contain" />
                  </a>
                ) : (
                  <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--ct-text-disabled)' }} />
                )}
              </div>
              <div className="p-2 space-y-1 text-xs">
                {p.description && <div>{p.description}</div>}
                <div className="flex items-center gap-1 flex-wrap">
                  {p.defectType && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
                      style={{ background: 'var(--ct-bg-secondary)', color: severityColor(p.severity) }}>
                      <AlertTriangle className="w-3 h-3" /> {p.defectType}
                      {p.severity && ` (${severityLabel(p.severity)})`}
                    </span>
                  )}
                  <span style={{ color: 'var(--ct-text-secondary)' }}>
                    · {p.uploadedByName || '—'} · {new Date(p.uploadedAt).toLocaleDateString(t('common.date-locale'))}
                  </span>
                </div>
                <button
                  onClick={() => handleDelete(p.id)}
                  className="text-xs flex items-center gap-1 hover:opacity-80 mt-1"
                  style={{ color: 'var(--ct-error)' }}
                >
                  <Trash2 className="w-3 h-3" /> {t('intakes.photos-btn-delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TabButton({ tab, active, onClick }) {
  const { t } = useTranslation();
  const Icon = tab.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors"
      style={{
        color: active ? 'var(--ct-accent)' : 'var(--ct-text-secondary)',
        borderBottom: active ? '2px solid var(--ct-accent)' : '2px solid transparent',
      }}
    >
      <Icon className="w-4 h-4" />
      {t(tab.labelKey)}
    </button>
  );
}

function TimelineEvent({ icon: Icon, tone, at, title, description }) {
  const { t } = useTranslation();
  const toneColors = {
    info: 'var(--ct-info)',
    success: 'var(--ct-success)',
    warning: 'var(--ct-warning)',
  };
  const color = toneColors[tone] || 'var(--ct-text-secondary)';
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--ct-bg-secondary)', border: `2px solid ${color}` }}
        >
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <div className="flex-1 w-px mt-1" style={{ background: 'var(--ct-border)' }} />
      </div>
      <div className="flex-1 pb-5 -mt-0.5">
        <div className="text-sm font-medium" style={{ color: 'var(--ct-text)' }}>{title}</div>
        {description && (
          <div className="text-xs mt-0.5" style={{ color: 'var(--ct-text-secondary)' }}>{description}</div>
        )}
        {at && (
          <div className="text-xs font-mono mt-1" style={{ color: 'var(--ct-text-disabled)' }}>
            {new Date(at).toLocaleString(t('common.date-locale'))}
          </div>
        )}
      </div>
    </div>
  );
}

function OverviewTab({ intake }) {
  const { t } = useTranslation();
  const instruments = intake.instruments || [];
  return (
    <div className="space-y-6">
      {/* Protocol info */}
      <section>
        <h3 className="ct-eyebrow mb-2">{t('intakes.overview-section-protocol')}</h3>
        <div className="ct-panel p-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="ct-eyebrow">{t('intakes.overview-label-protocol-number')}</div>
              <div className="font-mono" style={{ color: 'var(--ct-text)' }}>{intake.intakeProtocolNumber || '—'}</div>
            </div>
            <div>
              <div className="ct-eyebrow">{t('intakes.overview-label-received-by')}</div>
              <div style={{ color: 'var(--ct-text)' }}>{intake.receivedByName || '—'}</div>
            </div>
            <div>
              <div className="ct-eyebrow">{t('intakes.overview-label-customer')}</div>
              <div style={{ color: 'var(--ct-text)' }}>{intake.customerName || '—'}</div>
            </div>
            <div>
              <div className="ct-eyebrow">{t('intakes.overview-label-source-order')}</div>
              <div>
                {intake.orderNumber ? <RefLink type="order" id={intake.orderNumber} /> : '—'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instruments */}
      <section>
        <h3 className="ct-eyebrow mb-2">{t('intakes.overview-section-instruments', { count: instruments.length })}</h3>
        {instruments.length === 0 ? (
          <div className="ct-panel p-4">
            <div className="text-sm italic" style={{ color: 'var(--ct-text-disabled)' }}>{t('intakes.overview-no-instruments')}</div>
          </div>
        ) : (
          <div className="ct-panel overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'var(--ct-bg-secondary)' }}>
                  <th className="text-left py-2 px-3 ct-eyebrow">{t('intakes.overview-col-name')}</th>
                  <th className="text-left py-2 px-3 ct-eyebrow">{t('intakes.overview-col-type')}</th>
                  <th className="text-left py-2 px-3 ct-eyebrow">{t('intakes.overview-col-manufacturer')}</th>
                  <th className="text-left py-2 px-3 ct-eyebrow">{t('intakes.overview-col-serial')}</th>
                  <th className="text-left py-2 px-3 ct-eyebrow">{t('intakes.overview-col-marking')}</th>
                </tr>
              </thead>
              <tbody>
                {instruments.map((inst, idx) => (
                  <tr key={idx} style={{ borderTop: '1px solid var(--ct-border)' }}>
                    <td className="py-2 px-3">{inst.name || '—'}</td>
                    <td className="py-2 px-3 font-mono text-xs">{inst.type || '—'}</td>
                    <td className="py-2 px-3">{inst.manufacturer || '—'}</td>
                    <td className="py-2 px-3 font-mono text-xs">{inst.serialNumber || '—'}</td>
                    <td className="py-2 px-3 font-mono text-xs">
                      {[inst.marking1, inst.marking2].filter(Boolean).join(' · ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Notes */}
      {intake.notes && (
        <section>
          <h3 className="ct-eyebrow mb-2">{t('intakes.overview-section-notes')}</h3>
          <div className="ct-panel p-4 text-sm" style={{ color: 'var(--ct-text)', whiteSpace: 'pre-wrap' }}>
            {intake.notes}
          </div>
        </section>
      )}
    </div>
  );
}

function HistoryTab({ intake }) {
  const { t } = useTranslation();
  const events = [
    intake.receivedAt && {
      icon: Plus,
      tone: 'info',
      at: intake.receivedAt,
      title: t('intakes.history-event-created'),
      description: t('intakes.history-event-created-desc', {
        number: intake.intakeProtocolNumber || '—',
        by: intake.receivedByName ? t('intakes.history-event-created-by-suffix', { name: intake.receivedByName }) : '',
      }),
    },
    intake.sentToCalibrationAt && {
      icon: Send,
      tone: 'success',
      at: intake.sentToCalibrationAt,
      title: t('intakes.history-event-sent'),
      description: t('intakes.history-event-sent-desc'),
    },
  ].filter(Boolean);

  if (events.length === 0) {
    return <EmptyState icon={Clock} title={t('intakes.history-empty')} description={t('intakes.history-empty-desc')} />;
  }

  return (
    <div className="ct-panel p-4">
      {events.map((e, idx) => (
        <TimelineEvent key={idx} {...e} />
      ))}
    </div>
  );
}

function RelatedTab({ intake, calibrations }) {
  const { t } = useTranslation();
  const hasOrder = Boolean(intake.orderId);
  const hasCalibrations = calibrations.length > 0;

  return (
    <div className="space-y-4">
      <section>
        <h3 className="ct-eyebrow mb-2">{t('intakes.related-section-source-order')}</h3>
        {hasOrder ? (
          <div className="ct-panel p-4 flex items-center justify-between gap-3">
            <div className="min-w-0 flex items-center gap-3">
              <RefLink type="order" id={intake.orderNumber} />
              <span className="text-sm truncate" style={{ color: 'var(--ct-text-secondary)' }}>
                {intake.customerName || ''}
              </span>
            </div>
          </div>
        ) : (
          <EmptyState
            icon={LinkIcon}
            title={t('intakes.related-no-source')}
            description={t('intakes.related-no-source-desc')}
            size="sm"
          />
        )}
      </section>

      <section>
        <h3 className="ct-eyebrow mb-2">{t('intakes.related-section-calibrations', { count: calibrations.length })}</h3>
        {hasCalibrations ? (
          <div className="ct-panel overflow-hidden">
            {calibrations.map((c, idx) => (
              <div
                key={c.id}
                className="px-4 py-2.5 text-sm flex items-center justify-between"
                style={{ borderTop: idx === 0 ? 'none' : '1px solid var(--ct-border)' }}
              >
                <div className="min-w-0 flex-1 truncate">
                  <span style={{ color: 'var(--ct-text)' }}>{c.instrumentName || '—'}</span>
                  <span className="text-xs ml-2" style={{ color: 'var(--ct-text-secondary)' }}>
                    {c.laboratory} {c.methodologyCode ? `· ${c.methodologyCode}` : ''}
                  </span>
                </div>
                <StatusBadge status={c.status} className="flex-shrink-0" />
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={LinkIcon}
            title={t('intakes.related-no-calibrations')}
            description={
              intake.sentToCalibration
                ? t('intakes.related-no-calibrations-sent')
                : t('intakes.related-no-calibrations-not-sent')
            }
            size="sm"
          />
        )}
      </section>
    </div>
  );
}

export default function IntakeDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [intake, setIntake] = useState(null);
  const [calibrations, setCalibrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    api.get(`/intakes/${id}`)
      .then(r => { if (!cancelled) setIntake(r.data); })
      .catch(err => { if (!cancelled) setNotFound(err.response?.status === 404); })
      .finally(() => { if (!cancelled) setLoading(false); });

    api.get('/calibrations').then(r => {
      if (cancelled) return;
      setCalibrations(r.data.filter(c => c.intakeId === id));
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [id]);

  const downloadProtocol = () => {
    const token = localStorage.getItem('ct-token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const url = `${baseUrl}/api/intakes/${id}/protocol.pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${intake?.intakeProtocolNumber || 'protokol'}.pdf`;
        a.click();
      });
  };

  if (loading) return <LoadingState message={t('intakes.detail-loading-page')} />;
  if (notFound || !intake) {
    return (
      <EmptyState
        icon={PackageCheck}
        title={t('intakes.detail-not-found-title')}
        description={t('intakes.detail-not-found-desc')}
        action={<Button onClick={() => navigate('/intakes')} className="ct-btn-secondary">{t('intakes.detail-btn-back-list')}</Button>}
      />
    );
  }

  return (
    <div data-testid="intake-detail-page" className="space-y-4 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate('/intakes')}
            className="flex items-center gap-1 text-sm transition-colors hover:opacity-80"
            style={{ color: 'var(--ct-text-secondary)' }}
            title={t('intakes.detail-btn-back-list-title')}
          >
            <ArrowLeft className="w-4 h-4" />
            {t('intakes.detail-btn-back-list-label')}
          </button>
          <div className="flex items-baseline gap-3 min-w-0">
            <h1 className="ct-h1 font-mono truncate" style={{ color: 'var(--ct-accent)' }}>
              {intake.intakeProtocolNumber || '—'}
            </h1>
            <span className="text-sm truncate" style={{ color: 'var(--ct-text-secondary)' }}>
              {intake.customerName || t('intakes.detail-no-customer')}
            </span>
          </div>
          <StatusBadge
            tone={intake.sentToCalibration ? 'success' : 'warning'}
            className="flex-shrink-0"
          >
            {intake.sentToCalibration ? t('intakes.detail-status-sent') : t('intakes.detail-status-ready')}
          </StatusBadge>
        </div>
        <div className="flex items-center gap-2">
          {intake.generateProtocol && (
            <button
              type="button"
              onClick={downloadProtocol}
              className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border transition-colors"
              style={{ background: 'var(--ct-surface)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
              title={t('intakes.detail-btn-protocol-title')}
            >
              <Download className="w-3.5 h-3.5" />
              <span>{t('intakes.detail-btn-protocol')}</span>
              <FileText className="w-3.5 h-3.5" style={{ color: 'var(--ct-text-disabled)' }} />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b" style={{ borderColor: 'var(--ct-border)' }}>
        {TABS.map(t => (
          <TabButton
            key={t.id}
            tab={t}
            active={activeTab === t.id}
            onClick={() => setActiveTab(t.id)}
          />
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'overview' && <OverviewTab intake={intake} />}
        {activeTab === 'progress' && <ProgressTab intake={intake} />}
        {activeTab === 'photos' && <PhotosTab intakeId={intake.id} />}
        {activeTab === 'history' && <HistoryTab intake={intake} />}
        {activeTab === 'related' && <RelatedTab intake={intake} calibrations={calibrations} />}
      </div>
    </div>
  );
}
